/**
 * 对话图库。顶部收藏爱心右边的图像图标打开这一页。
 *
 * 默认列双方发送过的全部图片；“角色 / 我的”筛选是服务端查询条件，
 * 不是客户端在已加载的一页里假筛选。否则第 31 张才出现的角色图片
 * 会被误读成“一张都没有”。
 *
 * 每个筛选档用 key 挂一棵全新的 GalleryResults：`useResource` 在依赖变化后
 * 要到 effect 才会回 loading，原地复用会让新选项卡短暂闪过上一档的图。
 */

import { useCallback, useState } from "react";
import { EmptyState, LoadingState, PageNotice } from "../../components/AsyncState";
import { IconImage } from "../../components/Icons";
import { AssetImage } from "../../components/MessagePartRenderer";
import { Button, PageShell } from "../../components/ui";
import {
  fetchImageGallery,
  type GalleryImage,
  type ImageGallery,
  type ImageGallerySource,
} from "../../services/me-api";
import { useResource } from "../../state/resource";
import { useAppearance } from "../../state/session";
import "../pages.css";

function GalleryResults({
  source,
  characterName,
}: {
  source: ImageGallerySource;
  characterName: string;
}) {
  const data = useResource<ImageGallery>(
    (signal) => fetchImageGallery(source, signal),
    [source],
  );
  const [morePages, setMorePages] = useState<GalleryImage[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  const list = data.state.phase === "ready" ? data.state.data : null;
  const images = [...(list?.images ?? []), ...morePages];
  const total = list?.total ?? 0;
  const hasMore = images.length < total;

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setMoreFailed(false);
    fetchImageGallery(source, undefined, images.length)
      .then((page) => setMorePages((prev) => [...prev, ...page.images]))
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [images.length, source]);

  if (data.state.phase === "loading") {
    return <LoadingState label="正在收拢图片" />;
  }

  if (data.state.phase === "failed") {
    return (
      <PageNotice
        scene="gallery"
        error={data.state.error}
        onRetry={data.reload}
        expected={[
          "对话里双方发送过的图片",
          `按${characterName}和我的两个来源筛选`,
          "点开图片查看和下载原图",
        ]}
      />
    );
  }

  if (images.length === 0) {
    const title =
      source === "assistant"
        ? `${characterName}还没有发过图片`
        : source === "user"
          ? "你还没有发过图片"
          : "你们还没有分享过图片";
    return <EmptyState title={title} detail="在对话里发出的图，会留在这里。" />;
  }

  return (
    <>
      <p className="gallery-count">共 {total} 张，已显示 {images.length} 张</p>
      <div className="gallery-grid">
        {images.map((item) => {
          const sender = item.role === "assistant" ? characterName : "我";
          return (
            <article className="gallery-item" key={item.id}>
              <AssetImage assetId={item.assetId} alt={item.alt} />
              <p className="gallery-item__meta">
                <span>{sender}</span>
                <time dateTime={item.sentAt}>{item.sentAt.slice(0, 10)}</time>
              </p>
            </article>
          );
        })}
      </div>

      {hasMore ? (
        <Button block tone="quiet" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "正在翻出更早的…" : "看看更早的图片"}
        </Button>
      ) : null}

      {moreFailed ? <p className="inline-hint">没能翻出更早的图片，稍后再试试。</p> : null}
    </>
  );
}

export function ImageGalleryPage() {
  const appearance = useAppearance();
  const [source, setSource] = useState<ImageGallerySource>("all");
  const tabs: readonly { source: ImageGallerySource; label: string }[] = [
    { source: "all", label: "全部" },
    { source: "assistant", label: appearance.name },
    { source: "user", label: "我的" },
  ];

  return (
    <PageShell
      icon={<IconImage size={20} />}
      title="对话图库"
      help={
        <>
          <p>你和{appearance.name}发送过的图片，都会按时间收在这里。</p>
          <p>默认看全部，也可以只看{appearance.name}发的，或只看你发的。</p>
        </>
      }
    >
      <div className="gallery-tabs" role="tablist" aria-label="按图片发送方筛选">
        {tabs.map((tab) => (
          <button
            key={tab.source}
            type="button"
            role="tab"
            className={`gallery-tabs__item${source === tab.source ? " is-active" : ""}`}
            aria-selected={source === tab.source}
            onClick={() => setSource(tab.source)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <GalleryResults key={source} source={source} characterName={appearance.name} />
    </PageShell>
  );
}
